import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from 'components/mobile/primitives';
import { Button } from 'components/ui/button';
import type { FilterOption } from 'components/ui/filter-button';
import { cn } from 'lib/utils';

interface ChoiceRowsProps {
  name: string;
  options: FilterOption[];
  selected?: string;
  onSelect: (value: string | undefined) => void;
}

/** One choice per filter; tapping the chosen row again clears it. */
function ChoiceRows({ name, options, selected, onSelect }: ChoiceRowsProps) {
  return (
    <div role="radiogroup" aria-label={name} className="divide-y divide-muted">
      {options.map((option) => {
        const isSelected = option.value === selected;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(isSelected ? undefined : option.value)}
            className="flex h-11 w-full items-center gap-3 text-left text-[15px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <span
              aria-hidden="true"
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                isSelected ? 'border-foreground' : 'border-muted-foreground/40',
              )}
            >
              {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-foreground" />}
            </span>
            <span className={cn('min-w-0 flex-1 truncate', isSelected && 'font-semibold')}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

interface ChoiceTilesProps {
  name: string;
  options: FilterOption[];
  selected?: string;
  columns: 2 | 3;
  onSelect: (value: string) => void;
}

/** A grid of one-of-many tiles for the long lists (categories, subjects); `count` shows as "N ta test". */
function ChoiceTiles({ name, options, selected, columns, onSelect }: ChoiceTilesProps) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={name} className={cn('grid gap-2', columns === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
      {options.map((option) => {
        const isSelected = option.value.toLowerCase() === selected?.toLowerCase();
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(option.value)}
            className={cn(
              'flex min-h-[52px] flex-col justify-center rounded-xl border-[1.5px] px-3 py-2 text-left text-[13.5px] font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isSelected
                ? 'border-foreground bg-primary text-primary-foreground'
                : 'border-border bg-card text-foreground hover:border-foreground/30',
            )}
          >
            <span>{option.label}</span>
            {option.count ? (
              <span className={cn('mt-0.5 text-xs font-medium', !isSelected && 'text-muted-foreground')}>
                {option.count} {t('pager_noun_tests')}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface CatalogFilterSheetProps {
  open: boolean;
  onClose: () => void;
  categoryOptions: FilterOption[];
  category: string;
  /** The subjects of the chosen category; empty when it has none. */
  subjectOptions: FilterOption[];
  subject?: string;
  levelOptions: FilterOption[];
  priceOptions: FilterOption[];
  level?: string;
  price?: string;
  onCategoryChange: (value: string) => void;
  onSubjectChange: (value: string | undefined) => void;
  onLevelChange: (value: string | undefined) => void;
  onPriceChange: (value: string | undefined) => void;
  onClear: () => void;
  /** Tests under the current filters — updates live behind the sheet. */
  total: number;
}

/** The phone form of the catalog's filters — Bo'lim, Fan, Daraja and Narx: they apply as they are picked. */
export function CatalogFilterSheet({
  open,
  onClose,
  categoryOptions,
  category,
  subjectOptions,
  subject,
  levelOptions,
  priceOptions,
  level,
  price,
  onCategoryChange,
  onSubjectChange,
  onLevelChange,
  onPriceChange,
  onClear,
  total,
}: CatalogFilterSheetProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    // The sheet positions itself inside its nearest positioned box; the portal page scrolls, so pin one.
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalog-filter-sheet-title"
      tabIndex={-1}
      className="fixed inset-0 z-50 outline-none md:hidden"
    >
      <BottomSheet open onClose={onClose} maxHeight="88%">
        <div className="flex items-center justify-between">
          <h2 id="catalog-filter-sheet-title" className="text-[17px] font-bold text-foreground">
            {t('filters')}
          </h2>
          {(category !== 'all' || subject || level || price) && (
            <button
              type="button"
              onClick={onClear}
              className="h-9 text-sm font-medium text-foreground/80 hover:text-foreground hover:underline"
            >
              {t('clear')}
            </button>
          )}
        </div>

        <section className="mt-5">
          <h3 className="mb-2 text-[13px] font-semibold text-foreground/80">{t('portal_tests_categories')}</h3>
          <ChoiceTiles
            name={t('portal_tests_categories')}
            options={categoryOptions}
            selected={category}
            columns={2}
            onSelect={onCategoryChange}
          />
        </section>

        {subjectOptions.length > 0 && (
          <section className="mt-5">
            <h3 className="mb-2 text-[13px] font-semibold text-foreground/80">{t('portal_tests_subjects')}</h3>
            <ChoiceTiles
              name={t('portal_tests_subjects')}
              options={[{ value: 'all', label: t('all') }, ...subjectOptions]}
              selected={subject ?? 'all'}
              columns={3}
              onSelect={(value) => onSubjectChange(value === 'all' ? undefined : value)}
            />
          </section>
        )}

        <section className="mt-5">
          <h3 className="mb-1.5 text-[13px] font-semibold text-foreground/80">{t('portal_tests_filter_level')}</h3>
          <ChoiceRows name={t('portal_tests_filter_level')} options={levelOptions} selected={level} onSelect={onLevelChange} />
        </section>

        <section className="mt-5">
          <h3 className="mb-1.5 text-[13px] font-semibold text-foreground/80">{t('portal_tests_filter_price')}</h3>
          <ChoiceRows name={t('portal_tests_filter_price')} options={priceOptions} selected={price} onSelect={onPriceChange} />
        </section>

        <div className="sticky bottom-0 -mx-[18px] mt-6 border-t border-border bg-card px-[18px] pt-3">
          <Button size="lg" className="h-12 w-full text-[15px] font-semibold" onClick={onClose}>
            {t('filters_show_results', { n: total })}
          </Button>
        </div>
      </BottomSheet>
    </div>
  );
}

export default CatalogFilterSheet;
